import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";
import CandidatesActionTables from "../molecule/CandidatesActionTables";
import CandidateTable from "./CandidateTable";
import { API_BASE_URL } from "../../../config/api";

/* ================= helpers ================= */

const toKey = (v) => (v === undefined || v === null ? "" : String(v));

const deriveId = (rowOrId) => {
  if (rowOrId && typeof rowOrId === "object") {
    return (
      rowOrId.id ??
      rowOrId.candidateId ??
      rowOrId.applicationId ??
      rowOrId._id ??
      rowOrId.userId ??
      rowOrId.appId ??
      ""
    );
  }
  return rowOrId;
};

const normalizeRow = (r) => {
  const id =
    r?.id ??
    r?.candidateId ??
    r?.applicationId ??
    r?._id ??
    r?.userId ??
    r?.appId ??
    "";

  const row = {
    ...r,
    id: toKey(id),
    candidateId: r.candidateId ?? r.candidateid ?? r.id ?? "",
    applicationId: r.applicationId ?? r.applicationid ?? "",
    firstName: r.firstName ?? r.firstname ?? "",
    lastName: r.lastName ?? r.lastname ?? "",
    email: r.email ?? "",
    phone: r.phone ?? r.phonenumber ?? r.mobile ?? "",
    gender: r.gender ?? "",
    previousRole: r.previousRole ?? r.previousrole ?? r.position ?? "",
    applicationStatus:
      r.applicationStatus ?? r.applicationstatus ?? r.status ?? "Pending",
    jobId: r.jobid ?? r.jobId ?? "",
  };

  // build local search index
  const parts = [
    row.candidateId,
    row.applicationId,
    row.firstName,
    row.lastName,
    `${row.firstName} ${row.lastName}`.trim(),
    row.email,
    row.phone,
    row.gender,
    row.previousRole,
    row.applicationStatus,
  ]
    .filter(Boolean)
    .map(String);

  row._searchIndex = canon(parts.join(" | "));
  return row;
};

function canon(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

/* =============== component =============== */

const CandidatePage = () => {
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // docs modal state
  const [docsModalOpen, setDocsModalOpen] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsData, setDocsData] = useState(null);
  const [docsTitle, setDocsTitle] = useState("");

  // search state
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // /candidates?jobid=... and /candidates/:jobId
  const { jobId: jobIdFromPath } = useParams();
  const location = useLocation();
  const jobIdFromQuery = new URLSearchParams(location.search).get("jobid");
  const jobId = toKey(jobIdFromQuery || jobIdFromPath);

  const token = useMemo(() => localStorage.getItem("token"), []);
  const axiosCfg = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    [token]
  );

  /**
   * ✅ SCROLL FIX
   * If your app shell/layout sets `overflow: hidden` on html/body,
   * this page becomes “static” and you can’t scroll down.
   * We force-enable vertical scrolling while this page is mounted.
   */
  useEffect(() => {
    const prevBodyOverflowY = document.body.style.overflowY;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflowY = document.documentElement.style.overflowY;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    // enable scrolling
    document.body.style.overflowY = "auto";
    document.body.style.overflow = "auto";
    document.documentElement.style.overflowY = "auto";
    document.documentElement.style.overflow = "auto";

    return () => {
      // restore previous values
      document.body.style.overflowY = prevBodyOverflowY;
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflowY = prevHtmlOverflowY;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    if (!jobId) return;
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/applications/job/${encodeURIComponent(jobId)}`,
          axiosCfg
        );
        const list = res?.data?.data ?? res?.data ?? [];
        const normalized = Array.isArray(list) ? list.map(normalizeRow) : [];
        setCandidates(normalized);
      } catch (err) {
        console.error(
          "Error fetching applications for job:",
          err?.response?.data || err?.message
        );
        setCandidates([]);
      }
    })();
  }, [jobId, axiosCfg]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 150);
    return () => clearTimeout(t);
  }, [query]);

  const handleSearch = (value) => setQuery(value || "");

  const filteredCandidates = useMemo(() => {
    const q = canon(debouncedQuery);
    if (!q) return candidates;
    const terms = q.split(" ").filter(Boolean);
    return candidates.filter((row) => {
      const idx = row._searchIndex || "";
      return terms.every((t) => idx.includes(t));
    });
  }, [candidates, debouncedQuery]);

  const toggleSelect = (idOrRow) => {
    const rawId = deriveId(idOrRow);
    const id = toKey(rawId);
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // open a stored file using header-based fetch; fallback to openUrl with ?auth=
  const openStoredName = async (storedName, openUrl) => {
    let url = null;
    if (storedName) {
      url = `${API_BASE_URL}/api/applications/files/${encodeURIComponent(
        storedName
      )}`;
    } else if (openUrl) {
      const hasQuery = openUrl.includes("?");
      const withAuth = token
        ? `${openUrl}${hasQuery ? "&" : "?"}auth=${encodeURIComponent(token)}`
        : openUrl;
      window.open(withAuth, "_blank", "noopener,noreferrer");
      return;
    } else {
      return;
    }

    try {
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to open file");
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      window.open(href, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(href), 10000);
    } catch (e) {
      console.error("Open file error:", e);
    }
  };

  // View button handler (DOC-LINKS)
  const handleViewDocs = async ({
    jobId: jobIdFromRow,
    candidateId,
    fullName,
  }) => {
    const effectiveJobId = toKey(jobId || jobIdFromRow);
    if (!effectiveJobId || !candidateId) return;

    setDocsTitle(fullName ? `Documents – ${fullName}` : "Candidate Documents");
    setDocsModalOpen(true);
    setDocsLoading(true);
    setDocsData(null);

    try {
      const url = `${API_BASE_URL}/api/applications/job/${encodeURIComponent(
        effectiveJobId
      )}/doc-links/${encodeURIComponent(candidateId)}`;

      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      let payload = null;
      if (!res.ok) {
        // fallback to query style
        const qUrl = `${API_BASE_URL}/api/applications/job/${encodeURIComponent(
          effectiveJobId
        )}/doc-links?candidateId=${encodeURIComponent(candidateId)}`;
        const res2 = await fetch(qUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res2.ok) payload = await res2.json();
      } else {
        payload = await res.json();
      }

      const data = payload?.data || null;
      setDocsData(
        data || { cv: [], academic: [], education: [], work: [], certification: [] }
      );
    } catch (e) {
      console.error("Load docs error:", e);
      setDocsData({
        cv: [],
        academic: [],
        education: [],
        work: [],
        certification: [],
        error: "We couldn’t load the documents right now.",
      });
    } finally {
      setDocsLoading(false);
    }
  };

  const renderGroup = (title, items) => {
    const list = Array.isArray(items) ? items : [];
    return (
      <div className="mb-4">
        <div className="font-semibold text-gray-800 mb-2">{title}</div>
        {list.length === 0 ? (
          <div className="text-sm text-gray-500">No files</div>
        ) : (
          <ul className="space-y-2">
            {list.map((f, idx) => {
              const storedName = f?.storedName || null;
              const openUrl = f?.openUrl || null;
              const label = f?.filename || f?.storedName || `File ${idx + 1}`;
              return (
                <li
                  key={`${title}-${idx}`}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="truncate text-sm">{label}</span>
                  <button
                    className="px-3 py-1 text-sm font-medium text-black bg-[#FFCC00] rounded-md shadow hover:opacity-90"
                    onClick={() => openStoredName(storedName, openUrl)}
                    disabled={!storedName && !openUrl}
                    title="Open"
                  >
                    Open
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const getCvArray = (dd) => dd?.cv || [];

  return (
    <div className="w-full min-h-screen p-2 mb-2 bg-gray-50 overflow-y-auto">
      <h2 className="mb-2 text-2xl font-MTNfont">Candidates</h2>

      <div className="space-y-2 no-header-select">
        <CandidatesActionTables
          onSearch={handleSearch}
          selectedIds={selectedIds}
          jobId={jobId}
        />

        <CandidateTable
          candidates={filteredCandidates}
          selectedIds={selectedIds}
          toggleSelect={toggleSelect}
          showHeaderSelect={false}
          hideHeaderSelect
          hasHeaderSelect={false}
          onViewDocs={handleViewDocs}
        />
      </div>

      <style>{`
        .no-header-select thead input[type="checkbox"],
        .no-header-select thead .select-all,
        .no-header-select thead .header-select,
        .no-header-select thead .th-select {
          display: none !important;
        }
      `}</style>

      {/* Modal */}
      {docsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDocsModalOpen(false)}
          />

          <div className="relative w-[90%] max-w-3xl bg-white rounded shadow-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-[#FFCC00]">
              <h3 className="font-semibold text-black">
                {docsTitle || "Candidate Documents"}
              </h3>
              <button
                className="text-black/70 hover:text-black"
                onClick={() => setDocsModalOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* ✅ Make modal body scrollable so long lists don’t “freeze” */}
            <div className="p-5 max-h-[70vh] overflow-y-auto">
              {docsLoading ? (
                <div className="text-sm text-gray-600">Loading documents…</div>
              ) : docsData?.error ? (
                <div className="text-sm text-red-600">{docsData.error}</div>
              ) : (
                <>
                  {renderGroup("CV", getCvArray(docsData))}
                  {renderGroup("Academic", docsData?.academic)}
                  {renderGroup("Education", docsData?.education)}
                  {renderGroup("Work", docsData?.work)}
                  {renderGroup("Certification", docsData?.certification)}
                </>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-800 bg-gray-200 rounded-md hover:bg-gray-300"
                  onClick={() => setDocsModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidatePage;
